import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  ClipboardCopy,
  Loader2,
  RefreshCw,
  RotateCw,
  ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PickupComparisonView, PickupFieldComparison } from './pickupReconciliationPolicy';
import {
  fetchPickupDriftInspectorData,
  reverifyPickupLoan,
  type PickupDriftInspectorData,
  type PickupDriftInspectorTarget
} from './pickupDriftInspectorService';
import {
  PICKUP_COMPARISON_VIEW_OPTIONS,
  PICKUP_LOAN_CENTER_LABEL,
  PICKUP_OPTIMAL_BLUE_LABEL,
  pickupVerificationLabel,
  pickupVerificationSeverity,
  pickupVerificationSeverityClass,
  pickupVerificationShortLabel
} from './pickupVerificationPresentation';
import { PickupPricingRunsPanel } from './PickupPricingRunsPanel';
import {
  fetchPickupPricingRunHistoryPage,
  type PickupPricingRunHistoryPage
} from './pickupPricingRunHistoryService';
import {
  buildPickupPricingRuns,
  type PickupPricingRunSource
} from './pickupRunHistory';
import { formatPickupComparisonValue } from './pickupComparisonValueFormatter';

export type PickupDriftInspectorTab = 'drifts' | 'investor-pricing';

interface PickupDriftInspectorSheetProps {
  target: PickupDriftInspectorTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusElement?: HTMLElement | null;
  onRefreshCache?: () => Promise<void> | void;
  refreshCacheUnavailableReason?: string;
  onDataLoaded?: (data: PickupDriftInspectorData) => void;
  onOpenPipeline?: () => void;
  initialTab?: PickupDriftInspectorTab;
}

function inspectorTargetIdentity(target: PickupDriftInspectorTarget | null): string | null {
  if (target?.obLoanCacheId) {
    return `cache:${target.obLoanCacheId}`;
  }
  if (target?.obLoanId !== null && target?.obLoanId !== undefined) {
    return `ob:${target.obLoanId}`;
  }
  if (target?.encompassLoanId) {
    return `encompass:${target.encompassLoanId}`;
  }
  if (target?.loanNumber) {
    return `loan:${target.loanNumber}`;
  }
  return null;
}

function formatDateTime(value: string | null): string {
  if (!value) {
return 'Not verified yet';
}
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
return value;
}
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(parsed);
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatDrawerPrice(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toFixed(3);
}

function InspectorRibbonButton({
  label,
  count,
  active,
  countClassName,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  countClassName: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-pickup-ribbon-tab={label}
      className={cn(
        'relative flex w-full shrink-0 rotate-180 items-center justify-center gap-2 rounded-md border border-input bg-muted px-0 py-4 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground [writing-mode:vertical-rl] hover:border-primary hover:bg-card hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        active && 'border-primary bg-card font-bold text-primary shadow-[inset_-3px_0_0_hsl(var(--primary))]'
      )}
      onClick={onClick}
    >
      <span>{label}</span>
      <span
        data-pickup-ribbon-count={label}
        className={cn(
          'inline-flex min-h-5 min-w-5 rotate-180 items-center justify-center rounded-full border px-1 text-[9px] tabular-nums',
          countClassName
        )}
      >
        {count}
      </span>
    </button>
  );
}

function verdictLabel(row: PickupFieldComparison): string {
  if (row.verdict === 'missing_in_ob') {
    return 'Not in OB';
  }
  if (row.verdict === 'drift') {
    return row.tier === 'blocking' ? 'Blocking' : 'Differs';
  }
  return row.verdict === 'normalized_match' ? 'Same format' : 'Matches';
}

function ComparisonTable({ rows }: { rows: PickupFieldComparison[] }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
        No fields in this view.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full table-fixed border-collapse text-left text-[11.5px] leading-none">
        <thead className="bg-muted/60 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          <tr>
            <th scope="col" className="w-[30%] border-b border-r px-3 py-2 text-left font-semibold">Field</th>
            <th scope="col" className="w-[27%] border-b border-r px-3 py-2 text-right font-semibold">{PICKUP_LOAN_CENTER_LABEL}</th>
            <th scope="col" className="w-[27%] border-b border-r px-3 py-2 text-right font-semibold">{PICKUP_OPTIMAL_BLUE_LABEL}</th>
            <th scope="col" className="w-[16%] border-b px-3 py-2 text-right font-semibold">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const verdict = verdictLabel(row);
            const loanCenterValue = formatPickupComparisonValue(row.field, row.encompassRaw, 'encompass');
            const optimalBlueValue = formatPickupComparisonValue(row.field, row.obRaw, 'ob');
            return (
              <tr
                key={row.field}
                data-pickup-comparison-row={row.field}
                className={cn(
                  'border-b last:border-b-0',
                  verdict === 'Blocking' && 'bg-destructive/5',
                  verdict === 'Differs' && 'bg-warning/5'
                )}
              >
                <th scope="row" className="whitespace-normal break-words border-r px-[7px] py-[7px] align-top font-medium">
                  <div className="flex min-w-0 items-baseline gap-1">
                    <span className="shrink-0">{row.label}</span>
                    {row.note && (
                      <span
                        className="min-w-0 truncate text-[10px] font-normal text-muted-foreground"
                        title={row.note}
                      >
                        · {row.note}
                      </span>
                    )}
                  </div>
                </th>
                <td className="min-w-0 border-r px-[7px] py-[7px] text-right align-top tabular-nums">
                  <div className="truncate whitespace-nowrap" title={loanCenterValue}>{loanCenterValue}</div>
                </td>
                <td className="min-w-0 border-r px-[7px] py-[7px] text-right align-top tabular-nums">
                  <div
                    className={cn('truncate whitespace-nowrap', row.verdict === 'missing_in_ob' && 'text-muted-foreground')}
                    title={optimalBlueValue}
                  >
                    {optimalBlueValue}
                  </div>
                </td>
                <td className="px-[7px] py-[7px] text-right align-top">
                  <Badge
                    variant="outline"
                    title={row.verdict === 'normalized_match' ? 'Same value, different format' : undefined}
                    className={cn(
                      'h-5 px-1.5 text-[9px]',
                      verdict === 'Blocking' && 'border-destructive/50 bg-destructive/10 text-destructive',
                      verdict === 'Differs' && 'border-warning/60 bg-warning/10 text-warning-strong',
                      verdict === 'Not in OB' && 'border-muted-foreground/40 bg-muted text-muted-foreground',
                      (verdict === 'Matches' || verdict === 'Same format') && 'border-border bg-muted/60 text-muted-foreground'
                    )}
                  >
                    {verdict}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function copySummary(data: PickupDriftInspectorData): string {
  const differences = data.comparisons.filter(({ verdict }) => (
    verdict === 'drift' || verdict === 'missing_in_ob'
  ));
  return [
    `${PICKUP_OPTIMAL_BLUE_LABEL} comparison — ${data.loanNumber}`,
    `Borrower: ${data.borrowerName}`,
    `Status: ${pickupVerificationLabel(data.verificationStatus)}`,
    `Last verified: ${formatDateTime(data.lastVerifiedAt)}`,
    data.consequence,
    '',
    ...differences.map((row) => (
      `${row.label}: ${PICKUP_LOAN_CENTER_LABEL} = ${formatPickupComparisonValue(row.field, row.encompassRaw, 'encompass')} | ${PICKUP_OPTIMAL_BLUE_LABEL} = ${formatPickupComparisonValue(row.field, row.obRaw, 'ob')}${row.note ? ` | ${row.note}` : ''}`
    ))
  ].join('\n');
}

export function PickupDriftInspectorSheet({
  target,
  open,
  onOpenChange,
  returnFocusElement = null,
  onRefreshCache,
  refreshCacheUnavailableReason,
  onDataLoaded,
  onOpenPipeline,
  initialTab = 'drifts'
}: PickupDriftInspectorSheetProps) {
  const [data, setData] = useState<PickupDriftInspectorData | null>(null);
  const [activeTab, setActiveTab] = useState<PickupDriftInspectorTab>(initialTab);
  const [view, setView] = useState<PickupComparisonView | null>(null);
  const [loading, setLoading] = useState(false);
  const [reverifying, setReverifying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricingRunSources, setPricingRunSources] = useState<PickupPricingRunSource[]>([]);
  const [pricingRunsLoading, setPricingRunsLoading] = useState(false);
  const [pricingRunsLoadingMore, setPricingRunsLoadingMore] = useState(false);
  const [pricingRunsError, setPricingRunsError] = useState<string | null>(null);
  const [pricingRunsHasMore, setPricingRunsHasMore] = useState(false);
  const [pricingRunsNextOffset, setPricingRunsNextOffset] = useState<number | null>(null);
  const requestId = useRef(0);
  const pricingRunsRequestId = useRef(0);
  const activeIdentityRef = useRef<string | null>(null);
  const targetRef = useRef(target);
  const onDataLoadedRef = useRef(onDataLoaded);
  const targetIdentity = inspectorTargetIdentity(target);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
  }, [onDataLoaded]);

  const load = useCallback(async (expectedIdentity: string | null) => {
    const currentTarget = targetRef.current;
    if (!expectedIdentity
      || !currentTarget
      || inspectorTargetIdentity(currentTarget) !== expectedIdentity) {
      return;
    }
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    setLoading(true);
    setError(null);
    try {
      const nextData = await fetchPickupDriftInspectorData(currentTarget);
      if (requestId.current === currentRequest
        && activeIdentityRef.current === expectedIdentity
        && inspectorTargetIdentity(targetRef.current) === expectedIdentity) {
        setData(nextData);
        setView(nextData.defaultView);
        onDataLoadedRef.current?.(nextData);
      }
    } catch (nextError) {
      if (requestId.current === currentRequest
        && activeIdentityRef.current === expectedIdentity) {
        setError(nextError instanceof Error
          ? nextError.message
          : `Could not load the ${PICKUP_OPTIMAL_BLUE_LABEL} comparison.`);
      }
    } finally {
      if (requestId.current === currentRequest
        && activeIdentityRef.current === expectedIdentity) {
        setLoading(false);
      }
    }
  }, []);

  const loadPricingRuns = useCallback(async (
    expectedIdentity: string | null,
    offset = 0,
    append = false
  ) => {
    const currentTarget = targetRef.current;
    if (!expectedIdentity
      || !currentTarget?.obLoanId
      || inspectorTargetIdentity(currentTarget) !== expectedIdentity) {
      if (!append) {
        setPricingRunSources([]);
        setPricingRunsHasMore(false);
        setPricingRunsNextOffset(null);
      }
      return;
    }
    const currentRequest = pricingRunsRequestId.current + 1;
    pricingRunsRequestId.current = currentRequest;
    if (append) {
      setPricingRunsLoadingMore(true);
    } else {
      setPricingRunsLoading(true);
      setPricingRunsError(null);
    }
    try {
      const page: PickupPricingRunHistoryPage = await fetchPickupPricingRunHistoryPage({
        obLoanId: currentTarget.obLoanId,
        offset
      });
      if (pricingRunsRequestId.current !== currentRequest
        || activeIdentityRef.current !== expectedIdentity
        || inspectorTargetIdentity(targetRef.current) !== expectedIdentity) {
        return;
      }
      setPricingRunSources((previous) => {
        const combined = append ? [...previous, ...page.sources] : page.sources;
        const byRunId = new Map(combined.map((source) => [source.runId, source]));
        return [...byRunId.values()];
      });
      setPricingRunsHasMore(page.hasMore);
      setPricingRunsNextOffset(page.nextOffset);
      setPricingRunsError(null);
    } catch (nextError) {
      if (pricingRunsRequestId.current === currentRequest
        && activeIdentityRef.current === expectedIdentity) {
        setPricingRunsError(nextError instanceof Error
          ? nextError.message
          : 'Could not load pricing run history.');
      }
    } finally {
      if (pricingRunsRequestId.current === currentRequest
        && activeIdentityRef.current === expectedIdentity) {
        setPricingRunsLoading(false);
        setPricingRunsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    requestId.current += 1;
    pricingRunsRequestId.current += 1;
    activeIdentityRef.current = open ? targetIdentity : null;
    if (open && targetIdentity) {
      setActiveTab(initialTab);
      setData(null);
      setView(null);
      setError(null);
      setPricingRunSources([]);
      setPricingRunsError(null);
      setPricingRunsHasMore(false);
      setPricingRunsNextOffset(null);
      void load(targetIdentity);
      void loadPricingRuns(targetIdentity);
      return;
    }
    setLoading(false);
    setPricingRunsLoading(false);
    setPricingRunsLoadingMore(false);
  }, [initialTab, load, loadPricingRuns, open, targetIdentity]);

  const visibleRows = useMemo(() => {
    if (!data) {
return [];
}
    if (view === 'blocking') {
return data.comparisons.filter((row) => (
      row.tier === 'blocking' && (row.verdict === 'drift' || row.verdict === 'missing_in_ob')
    ));
}
    if (view === 'minor') {
return data.comparisons.filter((row) => (
      row.tier === 'advisory' && (row.verdict === 'drift' || row.verdict === 'missing_in_ob')
    ));
}
    return data.comparisons;
  }, [data, view]);
  const verificationBlocksPricing = data?.verificationStatus === 'waiting_for_ob_sync'
    || data?.verificationStatus === 'ob_mismatch'
    || data?.verificationStatus === 'ob_access_error';
  const pricingRuns = useMemo(
    () => buildPickupPricingRuns(
      pricingRunSources,
      target?.pricing?.opportunityAttempts || []
    ),
    [pricingRunSources, target?.pricing?.opportunityAttempts]
  );
  const latestPricingRun = pricingRuns[0] ?? null;
  const driftCount = data ? data.blockingCount + data.minorCount : 0;
  const comparisonCount = data?.comparisons.length ?? 0;
  const verificationChipInput = {
    status: data?.verificationStatus,
    mismatchFields: data?.comparisons
      .filter(({ verdict }) => verdict === 'drift' || verdict === 'missing_in_ob')
      .map(({ field, tier }) => ({ field, tier })),
    lastVerifiedAt: data?.lastVerifiedAt
  };
  const verificationChipSeverity = pickupVerificationSeverity(verificationChipInput);

  const handleCopy = async () => {
    if (!data) {
return;
}
    try {
      await navigator.clipboard.writeText(copySummary(data));
      toast.success('Drift summary copied.');
    } catch {
      toast.error('Could not copy the drift summary.');
    }
  };

  const handleReverify = async () => {
    const currentTarget = targetRef.current;
    const currentIdentity = inspectorTargetIdentity(currentTarget);
    if (!currentTarget?.obLoanId || !currentIdentity) {
      return;
    }
    setReverifying(true);
    try {
      const result = await reverifyPickupLoan(currentTarget.obLoanId);
      toast.success(result.message);
      await load(currentIdentity);
    } catch (nextError) {
      toast.error(nextError instanceof Error ? nextError.message : 'OB re-verification failed.');
    } finally {
      setReverifying(false);
    }
  };

  const handleRefresh = async () => {
    if (!onRefreshCache) {
return;
}
    setRefreshing(true);
    try {
      await onRefreshCache();
      await load(activeIdentityRef.current);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-pickup-drift-drawer
        data-pickup-drawer-tab={activeTab}
        className="grid h-full w-3/4 min-w-[452px] max-w-none grid-cols-[52px_minmax(0,1fr)] gap-0 overflow-hidden border-l-2 border-l-warning p-0 sm:max-w-none"
        onCloseAutoFocus={(event) => {
          if (returnFocusElement?.isConnected) {
            event.preventDefault();
            returnFocusElement.focus();
          }
        }}
      >
        <nav
          className="flex min-h-0 flex-col gap-1.5 border-r bg-muted/20 p-1.5"
          aria-label="Inspector sections"
          data-pickup-inspector-ribbon
        >
          <InspectorRibbonButton
            label="Drifts"
            count={driftCount}
            active={activeTab === 'drifts'}
            countClassName={data?.blockingCount
              ? 'border-destructive/50 bg-destructive/10 text-destructive'
              : data?.minorCount
                ? 'border-warning/60 bg-warning/10 text-warning-strong'
                : 'border-border bg-muted text-muted-foreground'}
            onClick={() => setActiveTab('drifts')}
          />
          <InspectorRibbonButton
            label="Pricing runs"
            count={pricingRuns.length}
            active={activeTab === 'investor-pricing'}
            countClassName="border-border bg-muted text-muted-foreground"
            onClick={() => setActiveTab('investor-pricing')}
          />
        </nav>
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
        <SheetHeader className="gap-0 border-b bg-muted/35 px-[18px] pb-[13px] pt-[15px] pr-12 text-left">
          <div className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
            {activeTab === 'investor-pricing' ? 'Pricing runs' : 'Drift inspector'}
          </div>
          <div
            className="mt-2 flex min-w-0 items-center gap-2 whitespace-nowrap"
            data-pickup-inspector-headline
          >
            <SheetTitle className="min-w-0 shrink truncate font-serif text-[15px] font-semibold leading-tight">
              {data?.loanNumber || target?.loanNumber || 'Loan'} · {data?.borrowerName || target?.borrowerName || 'Borrower unavailable'}
            </SheetTitle>
            {data && (
              <Badge variant="outline" className={cn('shrink-0', pickupVerificationSeverityClass(verificationChipSeverity))}>
                {pickupVerificationShortLabel(verificationChipInput)}
              </Badge>
            )}
            <SheetDescription className="min-w-0 flex-1 truncate text-[10.5px] leading-relaxed">
              {activeTab === 'drifts'
                ? `${comparisonCount} fields compared against the OB snapshot · last verified ${formatDateTime(data?.lastVerifiedAt ?? null)}`
                : latestPricingRun
                  ? `Repriced ${formatDateTime(latestPricingRun.analyzedAt)} · lock ${formatDrawerPrice(latestPricingRun.lockedPrice)} · ${latestPricingRun.basisLockPeriod ?? '—'}d basis`
                  : `No pricing run stored · last verified ${formatDateTime(data?.lastVerifiedAt ?? null)}`}
              {activeTab === 'investor-pricing' && data?.investorCommitment
                ? ` · ${data.investorCommitment}`
                : ''}
            </SheetDescription>
          </div>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-4 py-4">
            {activeTab === 'investor-pricing' && (
              <PickupPricingRunsPanel
                runs={pricingRuns}
                verificationBlocked={verificationBlocksPricing}
                loading={pricingRunsLoading}
                loadingMore={pricingRunsLoadingMore}
                error={pricingRunsError}
                hasMore={pricingRunsHasMore}
                onShowOlder={pricingRunsNextOffset === null
                  ? undefined
                  : () => void loadPricingRuns(
                    activeIdentityRef.current,
                    pricingRunsNextOffset,
                    true
                  )}
                onRetry={() => void loadPricingRuns(activeIdentityRef.current)}
              />
            )}
            {activeTab === 'drifts' && loading && (
              <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading current {PICKUP_LOAN_CENTER_LABEL} and {PICKUP_OPTIMAL_BLUE_LABEL} values…
              </div>
            )}
            {activeTab === 'drifts' && error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Couldn’t load the {PICKUP_OPTIMAL_BLUE_LABEL} comparison</AlertTitle>
                <AlertDescription className="mt-2 space-y-3">
                  <p>{error}</p>
                  <Button variant="outline" size="xs" onClick={() => void load(activeIdentityRef.current)}>Try again</Button>
                </AlertDescription>
              </Alert>
            )}
            {activeTab === 'drifts' && data && !data.hasObRecord && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No {PICKUP_OPTIMAL_BLUE_LABEL} record</AlertTitle>
                <AlertDescription>No {PICKUP_OPTIMAL_BLUE_LABEL} record is available for this loan yet.</AlertDescription>
              </Alert>
            )}
            {activeTab === 'drifts' && data?.hasObRecord && (
              <div>
                <Alert className={cn(
                  verificationBlocksPricing
                    ? 'border-destructive/60 bg-destructive/10'
                    : data.verificationStatus === 'verified_with_drift'
                      ? 'border-warning/60 bg-warning/10'
                      : 'border-success/50 bg-success/5'
                )}>
                  {verificationBlocksPricing
                    ? <ShieldAlert className="h-4 w-4" />
                    : data.verificationStatus === 'verified_with_drift'
                      ? <AlertCircle className="h-4 w-4" />
                      : <CheckCircle2 className="h-4 w-4" />}
                  <AlertTitle>{pickupVerificationLabel(data.verificationStatus)}</AlertTitle>
                  <AlertDescription data-pickup-drift-gate>
                    {data.consequence}
                    {data.missingInObCount > 0
                      ? ` This includes ${countLabel(data.missingInObCount, 'field', 'fields')} not returned by ${PICKUP_OPTIMAL_BLUE_LABEL}.`
                      : ''}
                  </AlertDescription>
                </Alert>
                <div
                  className="mb-[9px] mt-[13px] flex items-center justify-start gap-1"
                  aria-label="Comparison views"
                >
                  {PICKUP_COMPARISON_VIEW_OPTIONS.map((option) => {
                    const optionCount = option.value === 'all'
                      ? comparisonCount
                      : option.value === 'blocking'
                        ? data.blockingCount
                        : data.minorCount;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        data-pickup-drift-view={option.value}
                        aria-pressed={view === option.value}
                        disabled={loading}
                        className={cn(
                          'rounded-sm border-0 px-[9px] py-1 text-[10.5px] leading-[1.4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                          view === option.value
                            ? 'bg-primary font-semibold text-primary-foreground'
                            : 'bg-muted font-medium text-muted-foreground hover:bg-muted/80'
                        )}
                        onClick={() => setView(option.value)}
                      >
                        {option.label} <span className="tabular-nums">{optionCount}</span>
                      </button>
                    );
                  })}
                </div>
                <ComparisonTable rows={visibleRows} />
              </div>
            )}
          </div>
        </ScrollArea>

        <div
          data-pickup-drift-footer
          className="flex items-center gap-2 border-t bg-card px-[18px] py-3"
        >
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  className="w-8 shrink-0 px-0 text-muted-foreground"
                  aria-label="Copy drift summary"
                  onClick={() => void handleCopy()}
                  disabled={!data}
                >
                  <ClipboardCopy className="h-4 w-4" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy drift summary</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            size="xs"
            className="min-w-0 flex-1"
            disabled={!target?.obLoanId || reverifying || loading}
            onClick={() => void handleReverify()}
          >
            {reverifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />}
            Re-verify against OB
          </Button>
          <Button
            variant="outline"
            size="xs"
            disabled={!onRefreshCache || refreshing}
            title={refreshCacheUnavailableReason}
            aria-label={refreshCacheUnavailableReason
              ? `Refresh OB cache unavailable — ${refreshCacheUnavailableReason}`
              : 'Refresh OB cache'}
            onClick={() => void handleRefresh()}
          >
            {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh OB cache
          </Button>
          <Button variant="outline" size="xs" onClick={onOpenPipeline} disabled={!onOpenPipeline}>
            Open in Pipeline <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
